import { useCallback, useRef, useState } from 'react';

interface UseStepsHistoryResult<T> {
    push: (state: T) => void;
    undo: (currentState: T) => T | null;
    redo: (currentState: T) => T | null;
    clear: () => void;
    canUndo: boolean;
    canRedo: boolean;
}

export function useStepsHistory<T>(maxHistory = 30): UseStepsHistoryResult<T> {
    const pastRef = useRef<T[]>([]);
    const futureRef = useRef<T[]>([]);
    const [, setRevision] = useState(0);

    const push = useCallback(
        (state: T) => {
            pastRef.current.push(state);
            if (pastRef.current.length > maxHistory) pastRef.current.shift();
            futureRef.current = [];
            setRevision((r) => r + 1);
        },
        [maxHistory],
    );

    const undo = useCallback((currentState: T): T | null => {
        if (pastRef.current.length === 0) return null;
        futureRef.current.push(currentState);
        const prev = pastRef.current.pop()!;
        setRevision((r) => r + 1);
        return prev;
    }, []);

    const redo = useCallback((currentState: T): T | null => {
        if (futureRef.current.length === 0) return null;
        pastRef.current.push(currentState);
        const next = futureRef.current.pop()!;
        setRevision((r) => r + 1);
        return next;
    }, []);

    const clear = useCallback(() => {
        pastRef.current = [];
        futureRef.current = [];
        setRevision((r) => r + 1);
    }, []);

    return {
        push,
        undo,
        redo,
        clear,
        canUndo: pastRef.current.length > 0,
        canRedo: futureRef.current.length > 0,
    };
}
